import { useMemo, useState, KeyboardEvent } from 'react';
import {
  Autocomplete,
  Chip,
  TextField,
  Box,
  alpha,
  createFilterOptions,
} from '@mui/material';

const defaultFilter = createFilterOptions<string>();

interface MultiSelectChipsProps {
  options: string[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  label?: string;
  formatOption?: (value: string) => string;
  onUnknownInput?: (input: string) => void;
}

export default function MultiSelectChips({
  options,
  value,
  onChange,
  placeholder = '点击或搜索添加...',
  label,
  formatOption = (v) => v,
  onUnknownInput,
}: MultiSelectChipsProps) {
  const [inputValue, setInputValue] = useState('');

  const normalizedOptions = useMemo(
    () => Array.from(new Set((options || []).filter(Boolean))).sort(),
    [options]
  );

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && inputValue.trim()) {
      const normalized = inputValue.trim().toUpperCase();
      // Check if the input matches any option (case-insensitive)
      const exists = normalizedOptions.some(
        (opt) => opt.toUpperCase() === normalized
      );
      if (!exists && onUnknownInput) {
        event.preventDefault();
        onUnknownInput(normalized);
        setInputValue('');
      }
    }
  };

  return (
    <Autocomplete
      multiple
      options={normalizedOptions}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      getOptionLabel={formatOption}
      filterSelectedOptions
      filterOptions={(opts, state) => {
        // Only show options when user has typed at least 1 character
        if (!state.inputValue || state.inputValue.length === 0) {
          return [];
        }
        return defaultFilter(opts, state);
      }}
      noOptionsText={inputValue.length === 0 ? "输入字母开始搜索..." : "无匹配结果"}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : ''}
          size="small"
          onKeyDown={handleKeyDown}
          sx={{
            '& .MuiOutlinedInput-root': {
              minHeight: 44,
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.6),
            },
          }}
        />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              {...tagProps}
              label={formatOption(option)}
              size="small"
              sx={{
                maxWidth: 180,
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
          );
        })
      }
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box component="li" key={key} {...optionProps} sx={{ fontSize: '0.875rem' }}>
            {formatOption(option)}
          </Box>
        );
      }}
      sx={{
        '& .MuiAutocomplete-listbox': {
          maxHeight: 300,
        },
      }}
    />
  );
}
